'use client';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

type Props = {
  searchPlaceholder?: string;
  initialValue?: string;
  onColumnFiltersChange: Dispatch<SetStateAction<ColumnFiltersState>>;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
};

export function SearchInput({ onColumnFiltersChange, onPaginationChange, searchPlaceholder = 'Search...', initialValue = '' }: Props) {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  // Skip the debounce effect on first render — the URL already carries the
  // initial filter so the API call is already correct on mount.
  const isFirstRender = useRef(true);

  // Reset the guard on unmount (covers both real navigation and React 18
  // StrictMode's intentional unmount/remount cycle) so that the next mount
  // correctly skips the debounce on its own first render.
  useEffect(() => {
    return () => {
      isFirstRender.current = true;
    };
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timeout = setTimeout(() => {
      onColumnFiltersChange((prev: ColumnFiltersState) => {
        const withoutSearch = prev.filter((f) => f.id !== 'search');

        const trimmedSearchTerm = searchTerm.trim();

        if (trimmedSearchTerm === '') {
          return withoutSearch;
        }

        return [...withoutSearch, { id: 'search', value: trimmedSearchTerm }];
      });
      onPaginationChange((prev: PaginationState) => ({ ...prev, pageIndex: 0 }));
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTerm, onColumnFiltersChange, onPaginationChange]);

  return (
    <Input
      type="search"
      placeholder={searchPlaceholder}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      className={cn('w-full max-w-sm')}
    />
  );
}
